import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { AuthContext } from "../../../contexts/auth";
import useHistoricState from "../../../hooks/useHistoricState";
import MyModal from "../../../components/myModal";
import saveImage from "../../../utils/saveImage";
import MyImage from "../../../components/myImage";
import MyLabel from "../../../components/myLabel";
import MyButton from "../../../components/myButton";

const ActionName = ({ action }) => {
    let title = "Text Generator";
    let color = "#FFF";
    let background = "#6366f1";

    if (action === "Keyword") {
        title = "Keyword";
        background = "#8b5cf6";
    } else if (action === "Essay outline") {
        title = "Topic Generator";
        background = "#a855f7";
    } else if (action === "Product name generator") {
        title = "Name Product";
        background = "#ec4899";
    } else if (action === "Dalle") {
        title = "Image";
        background = "#06b6d4";
    } else if (action === "English grammar check") { 
        title = "English grammar";
        background = "#14b8a6";
    } else if (action === "Chat") {
        title = "Chat";
        background = "#9061EF";
    } else if (action === "Ad from product description") {
        title = "AD Generator";
        background = "#f97316";
    } else if (action === "Article post generator") {
        title = "Article";
        background = "#22c55e";
    }

    return (
        <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4px 12px',
            borderRadius: '16px',
            backgroundColor: background,
            color: color,
            fontSize: '12px',
            fontWeight: 500,
        }}>
            {title}
        </Box>
    );
};

const ItemList = ({ hist, onClick }) => {
    let send = null;
    try {
        send = JSON.parse(hist.send);
    } catch {
    }
    
    let title = send?.product;
    if (
        hist?.action === "Keyword" ||
        hist?.action === "Essay outline" ||
        hist?.action === "Dalle" ||
        hist?.action === "English grammar check" ||
        hist?.action === "Chat"
    ) {
        title = send?.text;
    } else if (hist.action === "Product name generator") {
        title = send?.description;
    }

    if (title?.length > 50) {
        title = title.slice(0, 50) + "...";
    }

    return (
        <ListItem disablePadding onClick={onClick} sx={{ mb: 1 }}>
            <ListItemButton 
                sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    borderRadius: '12px',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    padding: '16px 20px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        transform: 'translateY(-2px)',
                    }
                }} 
            >
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ 
                        fontSize: '15px', 
                        fontWeight: 500, 
                        color: '#333',
                        mb: 0.5
                    }}>
                        {title || 'No title'}
                    </Typography>
                    <Typography sx={{ 
                        fontSize: '12px', 
                        color: 'rgba(0,0,0,0.5)'
                    }}>
                        {hist.send_local_date}
                    </Typography>
                </Box>
                <Box>
                    <ActionName action={hist.action} />
                </Box>
            </ListItemButton>
        </ListItem>
    );
};

const BasicModal = ({ action, send, textReturn, onClick, children }) => {
    return (
        <Box sx={{ padding: '20px' }}>
            <Box sx={{ mb: 2 }}>
                <ActionName action={action} />
            </Box>
            <Box sx={{ 
                mb: 2, 
                p: 2, 
                backgroundColor: 'rgba(0,0,0,0.03)', 
                borderRadius: '12px' 
            }}>
                <Typography sx={{ fontSize: '14px', color: '#333' }}>
                    {send}
                </Typography>
            </Box>
            {children}
            <Box sx={{ 
                mb: 2, 
                p: 2, 
                backgroundColor: 'rgba(99,102,241,0.05)', 
                borderRadius: '12px',
                maxHeight: '200px',
                overflow: 'auto'
            }}>
                <Typography sx={{ fontSize: '14px', color: '#333', whiteSpace: 'pre-wrap' }}>
                    {textReturn}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <MyButton size="small" onClick={onClick} text="Retry" variant="contained" />
            </Box>
        </Box>
    );
};

const BasicModalOptions = ({ title, value }) => {
    return (
        <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
            <Typography sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.5)', minWidth: '80px' }}>
                {title}:
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#333' }}>
                {value}
            </Typography>
        </Box>
    );
};

const BodyModal = ({ setIsOpen, show, setHistoric, navigate }) => {
    if (!show) return null;
    
    let hist = {};
    try {
        hist = JSON.parse(show?.send);
    } catch {
    }

    const handleRepeat = (data, path) => {
        navigate(path);
        setHistoric(data);
        setIsOpen(false);
    };

    if (show.action === "Dalle") {
        let result = {};
        try {
            result = JSON.parse(show?.result);
        } catch {}
        let images = result["urls"] || [];
        
        return (
            <Box sx={{ padding: '20px' }}>
                <Box sx={{ mb: 2 }}>
                    <ActionName action={show.action} />
                </Box>
                <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: '12px' }}>
                    <Typography sx={{ fontSize: '14px', color: '#333' }}>
                        {hist?.text}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    {images.map((img, key) => (
                        <Box key={key} sx={{ textAlign: 'center' }}>
                            <MyImage src={img} alt={`Image ${key}`} sx={{ width: '150px', height: '150px', borderRadius: '12px', objectFit: 'cover' }} />
                            <MyButton size="small" onClick={() => saveImage(img)} text="Download" variant="outlined" sx={{ mt: 1 }} />
                        </Box>
                    ))}
                </Box>
                <Box sx={{ mb: 2 }}>
                    <BasicModalOptions title="Size" value={`${hist?.size} x ${hist?.size}`} />
                    <BasicModalOptions title="Date" value={show?.send_local_date} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <MyButton size="small" onClick={() => handleRepeat({ text: hist?.text, size: hist?.size, style: hist?.style }, '/image')} text="Retry" variant="contained" />
                </Box>
            </Box>
        );
    }

    const getConfig = () => {
        switch (show.action) {
            case "Ad from product description":
                return {
                    path: '/ad',
                    data: { product: hist?.product, tone: hist?.tone, type: hist?.type, platform: hist?.platform, target: hist?.target, ad: hist?.ad, sector: hist?.sector, the_amount: hist?.the_amount, language: hist?.language },
                    send: hist?.product,
                    options: [
                        { title: "Product", value: hist?.product },
                        { title: "Ad / Post", value: hist?.ad },
                        { title: "Tone", value: hist?.tone },
                        { title: "Platform", value: hist?.platform },
                    ]
                };
            case "Essay outline":
                return {
                    path: '/topics',
                    data: { text: hist?.text, type: hist?.type, target: hist?.target, language: hist?.language },
                    send: hist?.text,
                    options: [
                        { title: "Type", value: hist?.type },
                        { title: "Target", value: hist?.target },
                        { title: "Language", value: hist?.language },
                    ]
                };
            case "Keyword":
                return {
                    path: '/keywords',
                    data: { text: hist?.text, objective: hist?.objective, language: hist?.language },
                    send: hist?.text,
                    options: [
                        { title: "Objective", value: hist?.objective },
                        { title: "Language", value: hist?.language },
                    ]
                };
            case "Product name generator":
                return {
                    path: '/products',
                    data: { description: hist?.description, seed_words: hist?.seed_words, elements: hist?.elements, language: hist?.language },
                    send: hist?.description,
                    options: [
                        { title: "Seeds", value: hist?.seed_words },
                        { title: "Elements", value: hist?.elements },
                        { title: "Language", value: hist?.language },
                    ]
                };
            case "English grammar check":
                return {
                    path: '/translator',
                    data: { text: hist?.text },
                    send: hist?.text,
                    options: []
                };
            case "Chat":
                return {
                    path: '/chat',
                    data: { text: hist?.text },
                    send: hist?.text,
                    options: []
                };
            case "Article post generator":
                return {
                    path: '/articles',
                    data: { text: hist?.product, the_amount: hist?.the_amount, target: hist?.target, language: hist?.language, target_object: hist?.target_object, tone: hist?.tone },
                    send: hist?.text,
                    options: [
                        { title: "Type", value: hist?.target_object },
                        { title: "The Amount", value: hist?.the_amount },
                        { title: "Tone", value: hist?.tone },
                    ]
                };
            default:
                return null;
        }
    };

    const config = getConfig();
    if (!config) return null;

    return (
        <BasicModal
            action={show?.action}
            send={config.send}
            textReturn={show?.result}
            onClick={() => handleRepeat(config.data, config.path)}
        >
            <Box sx={{ mb: 2 }}>
                {config.options.map((opt, idx) => (
                    <BasicModalOptions key={idx} title={opt.title} value={opt.value} />
                ))}
                <BasicModalOptions title="Characters" value={show?.characteres} />
                <BasicModalOptions title="Date" value={show?.send_local_date} />
            </Box>
        </BasicModal>
    );
};

function HistoricPage() {
    const navigate = useNavigate();
    const { historics } = useHistoricState();
    const { historic } = useContext(AuthContext);
    const [isOpen, setIsOpen] = useState(false);
    const [show, setShow] = useState(null);

    return (
        <Fade in={true} timeout={500}>
            <Box sx={{ 
                width: '100%', 
                maxWidth: '800px', 
                margin: '0 auto', 
                padding: '24px',
                '@media (max-width: 600px)': { padding: '16px' }
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    mb: 3 
                }}>
                    <IconButton 
                        onClick={() => navigate('/dashboard')}
                        sx={{ 
                            backgroundColor: 'rgba(99,102,241,0.1)',
                            '&:hover': { backgroundColor: 'rgba(99,102,241,0.2)' }
                        }}
                        data-testid="button-back-dashboard"
                    >
                        <ArrowBackIcon sx={{ color: '#6366f1' }} />
                    </IconButton>
                    <Typography sx={{ 
                        fontSize: '24px', 
                        fontWeight: 600, 
                        color: '#333' 
                    }}>
                        Archive
                    </Typography>
                </Box>

                {historics.length > 0 ? (
                    <List sx={{ padding: 0 }}>
                        {historics.map((hist, key) => (
                            <ItemList
                                key={key}
                                onClick={() => {
                                    setShow(hist);
                                    setIsOpen(true);
                                }}
                                hist={hist}
                            />
                        ))}
                    </List>
                ) : (
                    <Box sx={{ 
                        textAlign: 'center', 
                        padding: '60px 20px',
                        backgroundColor: 'rgba(0,0,0,0.02)',
                        borderRadius: '16px'
                    }}>
                        <Typography sx={{ fontSize: '16px', color: 'rgba(0,0,0,0.5)' }}>
                            No history found. Start generating content to see your archive here.
                        </Typography>
                    </Box>
                )}

                <MyModal title="Historic" isOpen={isOpen} setIsOpen={setIsOpen} margin={0}>
                    <BodyModal
                        setIsOpen={setIsOpen}
                        show={show}
                        setHistoric={historic.set}
                        navigate={navigate}
                    />
                </MyModal>
            </Box>
        </Fade>
    );
}

export default HistoricPage;
